// ============================================================================
// CANCUN100 — ResumenTab.js  (FASE RESUMEN — panel de vendedores, admin, LECTURA)
// ----------------------------------------------------------------------------
// 100% SELECT sobre finanzas_ventas_agente, finanzas_pagos_semana y
// finanzas_folios. NO recalcula comisiones: todos los números vienen ya
// calculados de las vistas. PDFs con jsPDF cargado dinámicamente por CDN
// (misma infraestructura que la Lista de Precios; cero dependencias nuevas).
// ============================================================================
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import FRASES from './frases';
import PresentacionShow from './PresentacionShow';
import { generarResumenAgentePDF, corteCancun } from './pdf/resumenAgentePDF';
import { RADIO, RADIO_TARJETA } from './tema';

const F0 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const money = (n) => '$' + F0.format(Number(n || 0));
const num = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== '') return Number(o[k]) || 0; } return 0; };
const str = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== '') return String(o[k]); } return ''; };

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };
const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1]} ${y}`; };
const fraseTxt = (f) => (typeof f === 'string' ? f : (f && (f.frase || f.texto)) || String(f || ''));
const dosFrases = () => {
  if (!Array.isArray(FRASES) || FRASES.length === 0) return ['¡A darlo todo!', '¡Sigue así!'];
  const i1 = Math.floor(Math.random() * FRASES.length);
  let i2 = Math.floor(Math.random() * FRASES.length);
  if (FRASES.length > 1 && i2 === i1) i2 = (i2 + 1) % FRASES.length;
  return [fraseTxt(FRASES[i1]), fraseTxt(FRASES[i2])];
};

// ── Carga dinámica de jsPDF + autotable por CDN (una sola vez) ──
let pdfLoadingPromise = null;
const loadScript = (src) => new Promise((res, rej) => { const s = document.createElement('script'); s.src = src; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
const ensureJsPDF = async () => {
  if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
  if (!pdfLoadingPromise) {
    pdfLoadingPromise = (async () => {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
    })();
  }
  await pdfLoadingPromise;
  return window.jspdf.jsPDF;
};

// Agrega finanzas_ventas_agente por agente (suma semanas si el periodo es mes).
// Orden: por comisión generada desc (la comisión es la protagonista).
const aggVA = (rows) => {
  const map = {};
  rows.forEach((r) => {
    const id = String(str(r, ['agente_id']));
    if (!map[id]) map[id] = { agente_id: id, folios: 0, folios_casa: 0, folios_rp: 0, venta_total: 0, venta_casa: 0, venta_rp: 0, margen_total: 0, comision_generada: 0, comision_casa: 0, comision_rp: 0, comision_cobrable: 0, comision_bloqueada: 0, comision_ya_pagada: 0, cobrado: 0, saldo_pendiente: 0 };
    const g = map[id];
    g.folios += num(r, ['folios']); g.folios_casa += num(r, ['folios_casa']); g.folios_rp += num(r, ['folios_rp']);
    g.venta_total += num(r, ['venta_total']); g.venta_casa += num(r, ['venta_casa']); g.venta_rp += num(r, ['venta_rp']);
    g.margen_total += num(r, ['margen_total']);
    g.comision_generada += num(r, ['comision_generada']); g.comision_casa += num(r, ['comision_casa']); g.comision_rp += num(r, ['comision_rp']);
    g.comision_cobrable += num(r, ['comision_cobrable']); g.comision_bloqueada += num(r, ['comision_bloqueada']); g.comision_ya_pagada += num(r, ['comision_ya_pagada']);
    g.cobrado += num(r, ['cobrado']); g.saldo_pendiente += num(r, ['saldo_pendiente']);
  });
  return Object.values(map).sort((a, b) => b.comision_generada - a.comision_generada);
};

export default function ResumenTab({ C, agentes = [], chipAcceso = null }) { // B52-B: chipAcceso(agente_id) → chip «Pausa»/«Baja»
  const [loading, setLoading] = useState(true);
  const [va, setVa] = useState([]);        // finanzas_ventas_agente
  const [ps, setPs] = useState([]);        // finanzas_pagos_semana
  const [folios, setFolios] = useState([]); // finanzas_folios
  const [modo, setModo] = useState('semana'); // 'semana' | 'mes'
  const [anchor, setAnchor] = useState(() => new Date());
  const [busy, setBusy] = useState('');    // etiqueta de PDF en curso
  const [showPres, setShowPres] = useState(false); // overlay de presentación

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const [a, b, c] = await Promise.all([
        supabase.from('finanzas_ventas_agente').select('*'),
        supabase.from('finanzas_pagos_semana').select('*'),
        supabase.from('finanzas_folios_cliente').select('*'),
      ]);
      if (cancel) return;
      setVa(a.data || []); setPs(b.data || []); setFolios(c.data || []);
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  const agName = (id) => { const a = agentes.find((x) => String(x.id) === String(id)); return (a && a.nombre) ? a.nombre : `Agente #${id}`; };
  // Solo vendedores de sala en el tablero/ranking/PDFs/presentación.
  // Deja fuera a top_tours y a ventas_admin (Tere), que sí cuentan en Finanzas.
  const esSala = (id) => { const a = agentes.find((x) => String(x.id) === String(id)); return !!(a && a.tipo_agente === 'sala'); };

  // ── Periodo ──
  const weekStart = mondayOf(anchor);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 7);
  const ym = `${anchor.getFullYear()}-${pad(anchor.getMonth() + 1)}`;
  const periodoLabel = modo === 'semana'
    ? `${ymd(weekStart)} a ${ymd(new Date(weekEnd.getTime() - 86400000))}`
    : monthLabel(ym);
  const enSemanaCol = (v, col) => String(v[col] || '').slice(0, 10) === ymd(weekStart);
  const enMesCol = (v, col) => String(v[col] || '').slice(0, 7) === ym;
  const enPeriodoFecha = (ds) => { if (!ds) return false; if (modo === 'semana') { const d = new Date(ds); return d >= weekStart && d < weekEnd; } return String(ds).slice(0, 7) === ym; };

  const irSemana = () => { setModo('semana'); setAnchor(new Date()); };
  const irMes = () => { const d = new Date(); setModo('mes'); setAnchor(d.getDate() <= 5 ? new Date(d.getFullYear(), d.getMonth() - 1, 1) : new Date(d.getFullYear(), d.getMonth(), 1)); };
  const semanaPasada = () => { const m = mondayOf(new Date()); m.setDate(m.getDate() - 7); setAnchor(m); };
  const nav = (dir) => { const d = new Date(anchor); if (modo === 'semana') d.setDate(d.getDate() + dir * 7); else d.setMonth(d.getMonth() + dir); setAnchor(d); };

  // ── Datos del periodo ──
  const vaPeriodo = va.filter((r) => (modo === 'semana' ? enSemanaCol(r, 'semana_inicio') : enMesCol(r, 'semana_inicio')));
  // Incluir SOLO vendedores de sala (top_tours y ventas_admin quedan fuera).
  const ranking = aggVA(vaPeriodo).filter((r) => esSala(r.agente_id));
  const totalRow = ranking.reduce((t, r) => {
    ['folios', 'folios_casa', 'folios_rp', 'venta_casa', 'venta_rp', 'venta_total', 'comision_generada', 'comision_casa', 'comision_rp', 'comision_cobrable', 'comision_bloqueada'].forEach((k) => { t[k] = (t[k] || 0) + r[k]; });
    return t;
  }, {});

  const balances = ps
    .filter((r) => r.es_inicial === false && r.es_balance_semana_posterior === true)
    .filter((r) => (modo === 'semana' ? enSemanaCol(r, 'semana_pago') : enMesCol(r, 'semana_pago')))
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  const balancesTotal = balances.reduce((s, r) => s + num(r, ['monto']), 0);

  // ════════════════════════════ PDFs ════════════════════════════
  const ROSA = [247, 71, 128], NARANJA = [255, 140, 66], GRIS = [120, 120, 130];
  const footerCasa = (doc) => {
    const n = doc.internal.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      doc.setPage(i); doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(150, 150, 150);
      doc.text('Calle Tejon 14, Cancun Q.Roo. Mexico  |  info@cancun100.com  |  cancun100.com by Xpectative Travel', 14, 289);
      doc.text(`Pagina ${i} de ${n}`, 180, 289);
    }
  };

  const generarGlobalPDF = async () => {
    setBusy('global');
    try {
      const JsPDF = await ensureJsPDF();
      const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(...ROSA);
      doc.text('CANCUN100 - RESUMEN DE COMISIONES', 14, 16);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRIS);
      doc.text(`Periodo: ${periodoLabel}  ·  Generado: ${ymd(new Date())}`, 14, 22);
      doc.setDrawColor(...ROSA); doc.setLineWidth(0.4); doc.line(14, 25, 196, 25);

      const body = ranking.map((r, i) => [
        String(i + 1), agName(r.agente_id), `${r.folios} (${r.folios_casa}/${r.folios_rp})`,
        money(r.comision_casa), money(r.comision_rp), money(r.comision_generada), money(r.venta_total),
      ]);
      body.push(['', 'TOTAL', String(totalRow.folios || 0), money(totalRow.comision_casa), money(totalRow.comision_rp), money(totalRow.comision_generada), money(totalRow.venta_total)]);
      const lastIdx = body.length - 1;
      doc.autoTable({
        startY: 30,
        head: [['#', 'Vendedor', 'Folios', 'Com. casa', 'Com. RP', 'COMISION TOTAL', 'Venta bruta (ref.)']],
        body,
        styles: { fontSize: 12, cellPadding: 2.2 },
        headStyles: { fillColor: ROSA, textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' }, 2: { halign: 'center' },
          3: { halign: 'right' }, 4: { halign: 'right' },
          5: { halign: 'right', fontStyle: 'bold', textColor: NARANJA, fillColor: [255, 245, 238] },
          6: { halign: 'right', textColor: GRIS, fontSize: 12 },
        },
        theme: 'grid',
        didParseCell: (d) => { if (d.row.index === lastIdx) { d.cell.styles.fontStyle = 'bold'; if (d.column.index !== 5) d.cell.styles.fillColor = [245, 245, 247]; } },
      });
      footerCasa(doc);
      doc.save(`resumen_global_${ymd(new Date())}.pdf`);
    } catch (e) { alert('No se pudo generar el PDF: ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  const generarIndividualPDF = async (ag) => {
    setBusy(ag.agente_id);
    try {
      const [frase1, frase2] = dosFrases();
      // Histórico (todos los folios activos del agente, sin filtro de periodo).
      const foliosActivos = folios.filter((f) => String(f.agente_id) === String(ag.agente_id) && f.cancelado !== true);
      const liberadaPend = foliosActivos.reduce((s, f) => s + num(f, ['comision_liberada_no_pagada']), 0);
      const bloqueadaHist = foliosActivos.reduce((s, f) => s + num(f, ['comision_bloqueada']), 0);
      const foliosRows = foliosActivos.filter((f) => enPeriodoFecha(f.created_at)).map((f) => ({
        folio: str(f, ['folio_personalizado', 'folio']),
        cliente: str(f, ['cliente']),
        fecha: String(f.created_at || '').slice(0, 10),
        comision: num(f, ['comision_agente']),
        venta: num(f, ['total']),
      }));
      const balancesRows = balances.filter((r) => String(r.agente_id) === String(ag.agente_id)).map((r) => ({
        fecha: String(r.created_at || '').slice(0, 10),
        folio: str(r, ['folio']),
        monto: num(r, ['monto']),
        semanaVenta: String(r.semana_venta || '').slice(0, 10),
      }));
      await generarResumenAgentePDF({
        nombre: agName(ag.agente_id),
        periodoLabel,
        corte: corteCancun(),
        comisionGenerada: ag.comision_generada,
        ventaTotal: ag.venta_total,
        comisionYaPagada: ag.comision_ya_pagada,
        liberadaPend, bloqueadaHist,
        folios: foliosRows,
        balances: balancesRows,
        frase1, frase2,
      });
    } catch (e) { alert('No se pudo generar el PDF: ' + (e.message || e)); }
    finally { setBusy(''); }
  };

  // ════════════════ Estilos — sistema Aurelia por tokens ════════════════
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 20, marginBottom: 16 };
  const th = { textAlign: 'left', fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 500, padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, color: 'var(--au-text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', whiteSpace: 'nowrap' };
  const SecTitle = ({ n, title, sub }) => (
    <div style={{ margin: '26px 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 3, height: 18, background: 'var(--au-raya)', borderRadius: 2 }} />
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', margin: 0, fontFamily: 'var(--au-font-text)' }}>{n}. {title}</h2>
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--au-text-faint)', marginLeft: 11, marginTop: 3 }}>{sub}</div>}
    </div>
  );
  // pill del sistema (au-toggle): devuelve un <button aria-pressed>.
  const pill = (active, onClick, label, key) => (
    <button key={key} aria-pressed={active} onClick={onClick}>{label}</button>
  );
  const navBtn = (onClick, label) => (
    <button onClick={onClick} className="au-btn au-btn--secondary" style={{ padding: '6px 12px' }}>{label}</button>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 80 }}>
        <div style={{ width: 36, height: 36, border: '3px solid var(--au-glass-border)', borderTop: '3px solid var(--au-gold-solid)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ marginLeft: 14, color: 'var(--au-text-faint)', fontSize: 14 }}>Cargando resumen…</span>
      </div>
    );
  }

  const medalla = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : String(i + 1));

  return (
    <div>
      {/* ── Selector de periodo ── */}
      <div style={{ padding: '10px 0', borderBottom: '1px solid var(--au-glass-border)', marginBottom: 6, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="au-toggle">
          {pill(modo === 'semana', irSemana, 'Semana', 'sem')}
          {pill(modo === 'mes', irMes, 'Mes', 'mes')}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {navBtn(() => nav(-1), '◀')}
          <div className="au-num" style={{ fontSize: 14, fontWeight: 600, color: 'var(--au-text-primary)', minWidth: 150, textAlign: 'center' }}>{periodoLabel}</div>
          {navBtn(() => nav(1), '▶')}
        </div>
        {modo === 'semana' && <button onClick={semanaPasada} className="au-btn au-btn--ghost" style={{ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>Semana pasada</button>}
        <button onClick={() => setShowPres(true)} className="au-btn au-btn--secondary" style={{ marginLeft: 'auto' }}>🎬 Presentar</button>
        <button onClick={generarGlobalPDF} disabled={busy === 'global'} className="au-btn au-btn--secondary">
          {busy === 'global' ? 'Generando…' : '📄 PDF Global'}
        </button>
      </div>

      {showPres && (
        <PresentacionShow
          data={ranking.map((r) => ({ nombre: agName(r.agente_id), total: r.comision_generada, casa: r.comision_casa, rp: r.comision_rp }))}
          periodoLabel={periodoLabel}
          modo={modo}
          onClose={() => setShowPres(false)}
        />
      )}

      {/* ════ SECCIÓN A — Tablero de vendedores ════ */}
      <SecTitle n="A" title={`Tablero de vendedores · ${periodoLabel}`} sub="Ranking por comisión generada. La comisión total es la cifra estrella; la venta bruta es referencia. Clic en 📄 para el PDF individual (lo descargas tú y se lo envías)." />
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'center' }}>#</th>
              <th style={th}>Vendedor</th>
              <th style={{ ...th, textAlign: 'center' }}>Folios (casa/RP)</th>
              <th style={{ ...th, textAlign: 'right' }}>Com. casa</th>
              <th style={{ ...th, textAlign: 'right' }}>Com. RP</th>
              <th style={{ ...th, textAlign: 'right', color: C.naranja }}>Comisión total</th>
              <th style={{ ...th, textAlign: 'right' }}>Cobrable</th>
              <th style={{ ...th, textAlign: 'right' }}>Bloqueada</th>
              <th style={{ ...th, textAlign: 'right' }}>Venta (ref.)</th>
              <th style={{ ...th, textAlign: 'center' }}>PDF</th>
            </tr></thead>
            <tbody>
              {ranking.length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={10}>Sin ventas registradas en este periodo.</td></tr>}
              {ranking.map((r, i) => (
                <tr key={r.agente_id}>
                  <td style={{ ...td, textAlign: 'center', fontSize: 14 }}>{medalla(i)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{agName(r.agente_id)}{chipAcceso && chipAcceso(r.agente_id)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{r.folios} <span style={{ color: C.muted, fontSize: 12 }}>({r.folios_casa}/{r.folios_rp})</span></td>
                  <td style={{ ...td, textAlign: 'right' }}>{money(r.comision_casa)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{money(r.comision_rp)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span style={{ background: `${C.grad}`, color: '#fff', padding: '4px 11px', borderRadius: RADIO, fontWeight: 800, fontFamily: 'var(--au-font-text)', fontSize: 14, display: 'inline-block' }}>{money(r.comision_generada)}</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: C.green }}>{money(r.comision_cobrable)}</td>
                  <td style={{ ...td, textAlign: 'right', color: C.naranja }}>{money(r.comision_bloqueada)}</td>
                  <td style={{ ...td, textAlign: 'right', color: C.muted, fontSize: 12 }}>{money(r.venta_total)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button onClick={() => generarIndividualPDF(r)} disabled={busy === r.agente_id} className="au-btn au-btn--ghost" style={{ padding: '5px 10px' }}>
                      {busy === r.agente_id ? '…' : '📄'}
                    </button>
                  </td>
                </tr>
              ))}
              {ranking.length > 0 && (
                <tr style={{ background: `${C.blue}10` }}>
                  <td style={td}></td>
                  <td style={{ ...td, fontWeight: 800 }}>TOTAL</td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 800 }}>{totalRow.folios || 0} <span style={{ color: C.muted, fontSize: 12 }}>({totalRow.folios_casa || 0}/{totalRow.folios_rp || 0})</span></td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{money(totalRow.comision_casa)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{money(totalRow.comision_rp)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: C.naranja, fontFamily: 'var(--au-font-text)' }}>{money(totalRow.comision_generada)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: C.green }}>{money(totalRow.comision_cobrable)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: C.naranja }}>{money(totalRow.comision_bloqueada)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: C.muted, fontSize: 12 }}>{money(totalRow.venta_total)}</td>
                  <td style={td}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ════ SECCIÓN B — Balances recibidos ════ */}
      <SecTitle n="B" title="Balances recibidos en el periodo" sub="Pagos de folios de semanas anteriores — liberan comisión." />
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, color: C.muted }}>{balances.length} pago{balances.length !== 1 ? 's' : ''}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.green, fontFamily: 'var(--au-font-text)' }}>Total {money(balancesTotal)}</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead><tr>
              <th style={th}>Fecha</th><th style={th}>Folio</th><th style={th}>Agente</th>
              <th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Semana de venta original</th>
            </tr></thead>
            <tbody>
              {balances.length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={5}>Sin balances de semanas anteriores en este periodo.</td></tr>}
              {balances.slice(0, 400).map((r, i) => (
                <tr key={i}>
                  <td style={td}>{String(r.created_at || '').slice(0, 10)}</td>
                  <td style={{ ...td, fontFamily: 'var(--au-font-text)', fontWeight: 700 }}>{str(r, ['folio'])}</td>
                  <td style={td}>{agName(str(r, ['agente_id']))}{chipAcceso && chipAcceso(str(r, ['agente_id']))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: C.green }}>{money(num(r, ['monto']))}</td>
                  <td style={{ ...td, color: C.muted }}>{String(r.semana_venta || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ════ SECCIÓN C — Descargas ════ */}
      <SecTitle n="C" title="Descargas PDF" sub="Los PDFs los descargas tú (admin) para cotejarlos y enviarlos manualmente." />
      <div style={card}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={generarGlobalPDF} disabled={busy === 'global'} className="au-btn au-btn--secondary" style={{ padding: '12px 18px' }}>
            {busy === 'global' ? 'Generando…' : '📄 PDF Global del periodo'}
          </button>
          <div style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>El PDF individual motivador de cada vendedor está en el botón 📄 de su fila (Sección A).</div>
        </div>
      </div>
    </div>
  );
}
